import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import apiClient, { setCurrentTenantId, setAccessTokenGetter, setForceRefreshTokenGetter, setLogoutHandler } from '../lib/axios';
import { quickReplyApi } from '../features/templates/services/quickReplyApi';
import { templatesApi } from '../features/templates/services/templatesApi';
import { resetOptimisticState } from '../features/chat/hooks/useMessages';
import { TemplateApiContext, type TemplateApiServices } from '../features/templates/services/templateApiContext';

export interface Tenant {
  id: number;
  display_name: string;
  phone_number_id: string | null;
  waba_id: string | null;
  roles: string[];
  permissions: string[];
  features: TenantFeatures;
}

export interface TenantFeatures {
  isAiFaqEnabled: boolean;
  hideContactPhoneFromRestrictedRoles: boolean;
}

interface UserProfile {
  id: number;
  name: string;
  email: string;
  available_create_quota: number;
}

interface TenantContextValue {
  currentUserId: number | null;
  currentTenantId: number | null;
  accessibleTenants: Tenant[];
  tenantPermissions: string[];
  tenantRoles: string[];
  tenantFeatures: TenantFeatures;
  availableCreateQuota: number;
  switchTenant: (tenantId: number) => void;
  selectTenant: (tenantId: number) => void;
  clearTenantSelection: () => void;
  isBootstrapping: boolean;
  hasFetchedTenants: boolean;
  refreshTenants: () => Promise<void>;
}

export const TenantContext = createContext<TenantContextValue | null>(null);
export type { TenantContextValue };

const DEFAULT_FEATURES: TenantFeatures = {
  isAiFaqEnabled: false,
  hideContactPhoneFromRestrictedRoles: false,
};

export function TenantProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, getAccessTokenSilently, logout } = useAuth0();
  const queryClient = useQueryClient();

  const [accessibleTenants, setAccessibleTenants] = useState<Tenant[]>([]);
  const [currentUserId, setCurrentUserId] = useState<number | null>(null);
  const [currentTenantId, setCurrentTenantIdState] = useState<number | null>(null);
  const [isBootstrapping, setIsBootstrapping] = useState(false);
  const [hasFetchedTenants, setHasFetchedTenants] = useState(false);
  const [availableCreateQuota, setAvailableCreateQuota] = useState(0);

  useEffect(() => {
    if (isAuthenticated) {
      setAccessTokenGetter(() => getAccessTokenSilently());
      setForceRefreshTokenGetter(() => getAccessTokenSilently({ cacheMode: 'off' }));
    }
    setLogoutHandler(() => {
      localStorage.removeItem('letchat_tenant_id');
      logout({ logoutParams: { returnTo: globalThis.location.origin } });
    });
  }, [isAuthenticated, getAccessTokenSilently, logout]);

  const fetchTenants = useCallback(async () => {
    try {
      setIsBootstrapping(true);

      const [tenantsRes, profileRes] = await Promise.all([
        apiClient.get<Tenant[]>('/user/tenants'),
        apiClient.get<UserProfile>('/user/profile'),
      ]);

      const data = tenantsRes.data;
      setAccessibleTenants(data);
      setCurrentUserId(profileRes.data.id);
      setAvailableCreateQuota(profileRes.data.available_create_quota ?? 0);

      if (data.length === 1) {
        setCurrentTenantIdState(data[0].id);
        setCurrentTenantId(data[0].id);
        localStorage.setItem('letchat_tenant_id', String(data[0].id));
      } else if (data.length > 1) {
        const savedId = localStorage.getItem('letchat_tenant_id');
        const savedTenant = savedId ? data.find((t) => t.id === Number(savedId)) : null;
        if (savedTenant) {
          setCurrentTenantIdState(savedTenant.id);
          setCurrentTenantId(savedTenant.id);
        }
      }
    } catch {
      // User might not be provisioned yet
    } finally {
      setIsBootstrapping(false);
      setHasFetchedTenants(true);
    }
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchTenants();
    }
  }, [isAuthenticated, fetchTenants]);

  const removeTenantScopedQueries = useCallback(() => {
    queryClient.removeQueries({
      predicate: (query) => query.queryKey[0] !== 'notifications',
    });
    // Wipe module-level optimistic chat state so a 'sending' bubble or a
    // pendingOutboundContacts entry from the previous tenant can't linger and
    // (in the latter case) permanently suppress the Pusher echo. [FE-REL-04/FE-MNT-04]
    resetOptimisticState();
  }, [queryClient]);

  const applyTenant = useCallback(
    (tenantId: number) => {
      setCurrentTenantIdState(tenantId);
      setCurrentTenantId(tenantId);
      localStorage.setItem('letchat_tenant_id', String(tenantId));
      removeTenantScopedQueries();
    },
    [removeTenantScopedQueries],
  );

  const clearTenantSelection = useCallback(() => {
    setCurrentTenantIdState(null);
    setCurrentTenantId(null);
    localStorage.removeItem('letchat_tenant_id');
    removeTenantScopedQueries();
  }, [removeTenantScopedQueries]);

  const currentTenant = useMemo(
    () => accessibleTenants.find((t) => t.id === currentTenantId) ?? null,
    [accessibleTenants, currentTenantId],
  );

  const value = useMemo<TenantContextValue>(
    () => ({
      currentUserId,
      currentTenantId,
      accessibleTenants,
      tenantPermissions: currentTenant?.permissions ?? [],
      tenantRoles: currentTenant?.roles ?? [],
      tenantFeatures: currentTenant?.features ?? DEFAULT_FEATURES,
      availableCreateQuota,
      switchTenant: applyTenant,
      selectTenant: applyTenant,
      clearTenantSelection,
      isBootstrapping,
      hasFetchedTenants,
      refreshTenants: fetchTenants,
    }),
    [currentUserId, currentTenantId, accessibleTenants, currentTenant, availableCreateQuota, applyTenant, clearTenantSelection, isBootstrapping, hasFetchedTenants, fetchTenants],
  );

  const templateApiValue = useMemo<TemplateApiServices>(
    () => ({
      quickReply: quickReplyApi,
      waTemplate: templatesApi,
      scope: currentTenantId ? `tenant:${currentTenantId}` : 'tenant:none',
    }),
    [currentTenantId],
  );

  return (
    <TenantContext.Provider value={value}>
      <TemplateApiContext.Provider value={templateApiValue}>
        {children}
      </TemplateApiContext.Provider>
    </TenantContext.Provider>
  );
}

export function useTenant(): TenantContextValue {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within a TenantProvider');
  }
  return context;
}
